export interface DemandTask {
  demandId: string;
  generation: number;
}

/**
 * Gives a long-running demand task an identity that expires on navigation or
 * when a newer task begins. Async callers check the token after every await
 * before committing shared UI state.
 */
export function createDemandTaskGuard(currentDemandId: () => string | undefined): {
  begin: (demandId: string) => DemandTask;
  invalidate: () => void;
  isCurrent: (task: DemandTask) => boolean;
} {
  let generation = 0;
  return {
    begin: (demandId) => ({ demandId, generation: ++generation }),
    invalidate: () => {
      generation += 1;
    },
    isCurrent: (task) => task.generation === generation && currentDemandId() === task.demandId,
  };
}

/** Statuses a pricing-check run ends in: finished fresh, finished but already out of date, or failed. */
const TERMINAL_ANALYSIS_STATUSES = new Set(['done', 'stale', 'failed']);

/**
 * Pick the newest newly-created analysis only after it reaches a terminal state
 * (HS2-3XNHWH): `done`, `stale` (inputs edited mid-run, FR-5.8) or `failed`. A legacy
 * run paused for clarification (`waiting`) also ends the wait unless the caller is
 * following that paused version's resume (`pausedIsTerminal: false`).
 */
export function newTerminalAnalysis<T extends { id: string; status: string }>(
  rows: T[],
  previousIds: ReadonlySet<string>,
  { pausedIsTerminal = true }: { pausedIsTerminal?: boolean } = {},
): T | undefined {
  const created = rows.find((row) => !previousIds.has(row.id));
  if (created === undefined) return undefined;
  return TERMINAL_ANALYSIS_STATUSES.has(created.status) || (pausedIsTerminal && created.status === 'waiting')
    ? created
    : undefined;
}
